import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';
import { 
  Users, CheckCircle, ArrowRight, ArrowLeft, Shield, Clock, 
  FileCheck, Building2, Minus, Plus, Zap, Star
} from 'lucide-react';
import { apiFetch } from '../services/api';

const eur = (n) => new Intl.NumberFormat('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);

// ============================================================================
// STEP 1: PAKETAUSWAHL
// ============================================================================
const StepPackageSelection = ({ employeeCount, setEmployeeCount, billingCycle, setBillingCycle, pricing, onNext }) => {
  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-white mb-2">Wählen Sie Ihr Paket</h2>
        <p className="text-gray-400">Alle Schulungen inklusive • Jederzeit kündbar</p>
      </div>

      {/* Mitarbeiteranzahl */}
      <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700">
        <Label className="text-gray-300 text-lg mb-4 block text-center">Wie viele Mitarbeiter sollen geschult werden?</Label>
        <div className="flex items-center justify-center gap-4 mt-4">
          <button 
            onClick={() => setEmployeeCount(Math.max(1, employeeCount - 1))}
            data-testid="register-employees-minus"
            className="w-14 h-14 bg-gray-700 rounded-full flex items-center justify-center text-white hover:bg-gray-600 transition"
          >
            <Minus className="w-6 h-6" />
          </button>
          <input 
            type="number"
            data-testid="register-employees-input"
            value={employeeCount} 
            onChange={(e) => setEmployeeCount(Math.max(1, parseInt(e.target.value) || 1))} 
            className="w-28 bg-gray-900 border-2 border-yellow-500/50 rounded-xl px-4 py-4 text-white text-center text-3xl font-bold focus:border-yellow-500 focus:outline-none" 
          />
          <button 
            onClick={() => setEmployeeCount(employeeCount + 1)}
            data-testid="register-employees-plus" 
            className="w-14 h-14 bg-gray-700 rounded-full flex items-center justify-center text-white hover:bg-gray-600 transition"
          >
            <Plus className="w-6 h-6" />
          </button>
        </div>
        <p className="text-center text-gray-500 text-sm mt-3">
          <Users className="w-4 h-4 inline mr-1" />
          {employeeCount} {employeeCount === 1 ? 'Mitarbeiter' : 'Mitarbeiter'}
        </p>
      </div>

      {/* Preisübersicht */}
      {pricing && (
        <div className="bg-gradient-to-br from-yellow-500/10 to-transparent rounded-xl p-6 border border-yellow-500/30">
          <div className="text-center">
            {pricing.volume_discount_percent > 0 && (
              <div className="inline-block bg-green-500/20 text-green-400 text-xs px-3 py-1 rounded-full mb-3">
                {pricing.volume_discount_percent}% Mengenrabatt
              </div>
            )}
            <div className="text-4xl font-black text-yellow-500 mb-1">
              {eur(billingCycle === 'yearly' ? pricing.monthly_equivalent : pricing.monthly_total)} €
              <span className="text-lg text-gray-400 font-normal">/Monat</span>
            </div>
            <p className="text-gray-400 text-sm">
              {billingCycle === 'yearly' 
                ? `${pricing.yearly_total?.toFixed(2)}€/Jahr (${pricing.per_employee_yearly?.toFixed(2)}€ pro MA/Jahr)`
                : `${eur(pricing.per_employee_monthly)} € pro Mitarbeiter/Monat`
              }
            </p>
          </div>
        </div>
      )}

      {/* Features */}
      <div className="grid grid-cols-2 gap-3 text-sm">
        {[
          { icon: Shield, text: 'DGUV-konform' },
          { icon: FileCheck, text: 'Auto-Zertifikate' },
          { icon: Clock, text: '14 Tage kostenlos' },
          { icon: Zap, text: 'Sofortzugang' }
        ].map((f, i) => (
          <div key={i} className="flex items-center gap-2 text-gray-400">
            <f.icon className="w-4 h-4 text-yellow-500" />
            <span>{f.text}</span>
          </div>
        ))}
      </div>

      <Button 
        onClick={onNext} 
        className="w-full bg-gradient-to-r from-yellow-600 to-yellow-500 text-black font-bold py-6 text-lg hover:from-yellow-500 hover:to-yellow-400"
      >
        Weiter zur Registrierung <ArrowRight className="ml-2 w-5 h-5" />
      </Button>
    </div>
  );
};

// ============================================================================
// STEP 2: FIRMENDATEN
// ============================================================================
const StepCompanyDetails = ({ formData, setFormData, onNext, onBack, loading, error, employeeCount, billingCycle }) => {
  const isValid = formData.name && formData.email && formData.password && formData.password.length >= 8;

  // Live-Preisvorschau (entspricht der Preview im Konfigurator)
  const PRICE_MONTHLY = 10.90;
  const PRICE_YEARLY = 109.00;
  const seats = Math.max(1, employeeCount || 1);
  const isYearly = billingCycle === 'yearly';
  const grossAnnual = (isYearly ? PRICE_YEARLY : PRICE_MONTHLY * 12) * seats;
  let discount = 0;
  if (seats >= 50) discount = 0.20;
  else if (seats >= 20) discount = 0.15;
  else if (seats >= 10) discount = 0.10;
  const annual = grossAnnual * (1 - discount);
  const monthly = annual / 12;

  return (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <h2 className="text-2xl font-bold text-white mb-2">Ihre Firmendaten</h2>
        <p className="text-gray-400">Erstellen Sie Ihr Konto für Ihren Zugang</p>
      </div>

      {/* Preis-Sticker (was wird gleich gebucht) */}
      <div className="bg-gradient-to-r from-yellow-500/10 via-gray-900/50 to-gray-900/50 border border-yellow-500/30 rounded-xl p-4" data-testid="step2-price-summary">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <p className="text-gray-400 text-xs uppercase tracking-wide">Ihre Auswahl</p>
            <p className="text-white font-semibold">
              {seats} {seats === 1 ? 'Mitarbeiter-Lizenz' : 'Mitarbeiter-Lizenzen'} · {isYearly ? 'Jährlich' : 'Monatlich'}
              {discount > 0 && <span className="ml-2 text-green-400 text-xs">−{Math.round(discount * 100)} % Rabatt</span>}
            </p>
          </div>
          <div className="text-right">
            <div className="text-yellow-500 text-2xl font-bold leading-none">
              {monthly.toFixed(2).replace('.', ',')}€<span className="text-sm text-gray-400 font-normal">/Monat</span>
            </div>
            <div className="text-gray-500 text-xs mt-0.5">
              ≈ {(annual / 12 / seats).toFixed(2).replace('.', ',')}€ pro Mitarbeiter/Monat
            </div>
          </div>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-500/10 border border-red-500/40 rounded-xl p-4 flex items-start gap-3" data-testid="register-error">
          <div className="w-6 h-6 bg-red-500/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
            <span className="text-red-400 text-sm font-bold">!</span>
          </div>
          <div>
            <p className="text-red-400 font-medium text-sm">{error}</p>
            {(error.includes('bereits registriert') || error.toLowerCase().includes('passwort')) && (
              <p className="text-gray-400 text-xs mt-1">
                Bereits ein Konto? <a href="/login" className="text-yellow-500 hover:underline">Zum Login</a> &middot; <a href="/forgot-password" className="text-yellow-500 hover:underline">Passwort vergessen?</a>
              </p>
            )}
          </div>
        </div>
      )}

      <div className="space-y-4">
        <div>
          <Label htmlFor="name" className="text-gray-300">Firmenname *</Label>
          <Input
            id="name"
            type="text"
            required
            value={formData.name}
            onChange={(e) => setFormData({...formData, name: e.target.value})}
            className="bg-gray-800 border-gray-700 text-white focus:border-yellow-500"
            placeholder="Ihre Firma GmbH"
            data-testid="register-company-name"
          />
        </div>

        <div>
          <Label htmlFor="email" className="text-gray-300">E-Mail *</Label>
          <Input
            id="email"
            type="email"
            required
            value={formData.email}
            onChange={(e) => setFormData({...formData, email: e.target.value})}
            className={`bg-gray-800 border-gray-700 text-white focus:border-yellow-500 ${error?.includes('bereits registriert') ? 'border-red-500' : ''}`}
            placeholder="info@firma.de"
            data-testid="register-email"
          />
        </div>

        <div>
          <Label htmlFor="password" className="text-gray-300">Passwort * (min. 8 Zeichen)</Label>
          <Input
            id="password"
            type="password"
            required
            value={formData.password}
            onChange={(e) => setFormData({...formData, password: e.target.value})}
            className="bg-gray-800 border-gray-700 text-white focus:border-yellow-500"
            placeholder="Mindestens 8 Zeichen"
            data-testid="register-password"
          />
          {formData.password && formData.password.length < 8 && (
            <p className="text-red-400 text-xs mt-1">Mindestens 8 Zeichen erforderlich</p>
          )}
        </div>

        <div>
          <Label htmlFor="phone" className="text-gray-300">Telefon (optional)</Label>
          <Input
            id="phone"
            type="tel"
            value={formData.phone}
            onChange={(e) => setFormData({...formData, phone: e.target.value})}
            className="bg-gray-800 border-gray-700 text-white focus:border-yellow-500"
            placeholder="+49 123 456789"
            data-testid="register-phone"
          />
        </div>

        <div>
          <Label htmlFor="address" className="text-gray-300">Adresse (optional)</Label>
          <Input
            id="address"
            type="text"
            value={formData.address}
            onChange={(e) => setFormData({...formData, address: e.target.value})}
            className="bg-gray-800 border-gray-700 text-white focus:border-yellow-500"
            placeholder="Straße, PLZ, Stadt"
            data-testid="register-address"
          />
        </div>
      </div>

      <div className="flex gap-3">
        <Button 
          variant="outline" 
          onClick={onBack}
          className="flex-1 border-gray-700 text-gray-300 hover:bg-gray-800"
        >
          <ArrowLeft className="mr-2 w-4 h-4" /> Zurück
        </Button>
        <Button 
          onClick={onNext} 
          disabled={!isValid || loading}
          className="flex-1 bg-gradient-to-r from-yellow-600 to-yellow-500 text-black font-bold hover:from-yellow-500 hover:to-yellow-400 disabled:opacity-50"
          data-testid="register-submit-btn"
        >
          {loading ? 'Wird vorbereitet...' : 'Registrieren & Weiter zur Kasse'}
        </Button>
      </div>

      <p className="text-center text-gray-500 text-xs">
        Mit der Registrierung akzeptieren Sie unsere <a href="/legal/agb" className="text-yellow-500 hover:underline">AGB</a> und <a href="/legal/datenschutz" className="text-yellow-500 hover:underline">Datenschutzrichtlinien</a>
      </p>
    </div>
  );
};

// ============================================================================
// STEP 3: ERFOLG
// ============================================================================
const StepSuccess = ({ employeeCount, billingCycle, pricing }) => {
  const navigate = useNavigate();
  
  return (
    <div className="text-center space-y-6">
      <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto">
        <CheckCircle className="w-10 h-10 text-green-500" />
      </div>
      
      <div>
        <h2 className="text-2xl font-bold text-white mb-2">Willkommen bei Smartvolt Academy!</h2>
        <p className="text-gray-400">Ihre 14-tägige Testversion ist aktiv</p>
      </div>

      <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700 text-left space-y-3">
        <div className="flex justify-between">
          <span className="text-gray-400">Paket:</span>
          <span className="text-white font-medium">{employeeCount} Mitarbeiter</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-400">Abrechnungszyklus:</span>
          <span className="text-white font-medium">{billingCycle === 'yearly' ? 'Jährlich' : 'Monatlich'}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-400">Preis nach Testphase:</span>
          <span className="text-yellow-500 font-bold">
            {eur(billingCycle === 'yearly' ? pricing?.monthly_equivalent : pricing?.monthly_total)} €/Monat
          </span>
        </div>
        <div className="border-t border-gray-700 pt-3">
          <p className="text-green-400 text-sm flex items-center gap-2">
            <CheckCircle className="w-4 h-4" />
            Keine Zahlungsdaten erforderlich für die Testphase
          </p>
        </div>
      </div>

      <div className="space-y-3">
        <p className="text-gray-400 text-sm">Was Sie jetzt tun können:</p>
        <div className="grid gap-2 text-left">
          <div className="flex items-center gap-3 text-gray-300">
            <div className="w-6 h-6 bg-yellow-500/20 rounded-full flex items-center justify-center text-yellow-500 text-xs font-bold">1</div>
            <span>Mitarbeiter hinzufügen</span>
          </div>
          <div className="flex items-center gap-3 text-gray-300">
            <div className="w-6 h-6 bg-yellow-500/20 rounded-full flex items-center justify-center text-yellow-500 text-xs font-bold">2</div>
            <span>Kurse zuweisen</span>
          </div>
          <div className="flex items-center gap-3 text-gray-300">
            <div className="w-6 h-6 bg-yellow-500/20 rounded-full flex items-center justify-center text-yellow-500 text-xs font-bold">3</div>
            <span>Schulungsmatrix verwalten</span>
          </div>
        </div>
      </div>

      <Button 
        onClick={() => navigate('/dashboard')} 
        className="w-full bg-gradient-to-r from-yellow-600 to-yellow-500 text-black font-bold py-6 text-lg hover:from-yellow-500 hover:to-yellow-400"
        data-testid="go-to-dashboard-btn"
      >
        Zum Chef-Cockpit <ArrowRight className="ml-2 w-5 h-5" />
      </Button>
    </div>
  );
};

// ============================================================================
// HAUPTKOMPONENTE
// ============================================================================
const RegisterPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { registerOrLogin } = useAuth();
  const [searchParams] = useSearchParams();
  const refCode = searchParams.get('ref') || '';
  
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [registerError, setRegisterError] = useState('');
  const initialEmployees = parseInt(searchParams.get('employees') || '5', 10);
  const initialCycle = 'monthly';
  const [employeeCount, setEmployeeCount] = useState(Math.max(1, isNaN(initialEmployees) ? 5 : initialEmployees));
  const [billingCycle, setBillingCycle] = useState(initialCycle);
  const [pricing, setPricing] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    address: '',
    phone: ''
  });

  // Bei `?autocheckout=1` oder vorhandenem `employees`-Param direkt Step 2 zeigen,
  // damit der Nutzer nicht zwei Mal die Mengenwahl macht.
  useEffect(() => {
    if (searchParams.get('autocheckout') === '1' || searchParams.get('employees')) {
      setStep(2);
    }
  }, []); // eslint-disable-line

  // Preis berechnen
  useEffect(() => {
    const fetchPricing = async () => {
      try {
        const response = await apiFetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/subscriptions/calculate-price?employee_count=${employeeCount}&billing_cycle=${billingCycle}`
        );
        const data = await response.json();
        if (data.success) {
          setPricing(data.pricing);
        }
      } catch (error) {
        console.error('Pricing error:', error);
      }
    };
    fetchPricing();
  }, [employeeCount, billingCycle]);

  const handleSubmit = async () => {
    setLoading(true);
    setRegisterError('');
    
    try {
      const result = await registerOrLogin({
        ...formData,
        employeeCount,
        billingCycle,
        ref: refCode || undefined });
      
      if (result.success) {
        toast({
          title: result.existing ? "Willkommen zurück!" : "Konto erstellt!",
          description: result.existing
            ? "Wir leiten Sie direkt zum Digistore24-Checkout weiter."
            : "Weiterleitung zum Digistore24-Checkout..."
        });

        // Direkt zum Digistore24-Checkout weiterleiten mit Lizenzanzahl
        try {
          const params = new URLSearchParams({
            quantity: String(employeeCount),
            cycle: billingCycle });
          const checkoutRes = await apiFetch(
            `${process.env.REACT_APP_BACKEND_URL}/api/learning/checkout/basis?${params.toString()}`
          );
          const checkoutData = await checkoutRes.json();

          if (checkoutData.success && checkoutData.checkout_url) {
            // Vollredirect (kein neuer Tab) damit der Nutzer im Kaufprozess bleibt
            window.location.href = checkoutData.checkout_url;
            return;
          }
          // Fallback: Erfolgs-Step zeigen, falls Digistore24-Produkt nicht konfiguriert
          toast({
            title: "Checkout nicht verfügbar",
            description: checkoutData.message || "Bitte loggen Sie sich ein und buchen Sie aus dem Cockpit heraus.",
            variant: "destructive"
          });
          setStep(3);
        } catch (e) {
          toast({
            title: "Weiterleitung fehlgeschlagen",
            description: "Bitte loggen Sie sich ein und versuchen Sie es aus dem Cockpit erneut.",
            variant: "destructive"
          });
          setStep(3);
        }
      } else if (result.action === 'password_mismatch') {
        const errorMsg = result.error || 'Diese E-Mail ist bereits registriert. Bitte mit dem bestehenden Passwort anmelden.';
        setRegisterError(errorMsg);
        toast({
          title: "Bestehender Account",
          description: errorMsg,
          variant: "destructive"
        });
      } else {
        const errorMsg = result.error || 'Registrierung fehlgeschlagen. Bitte versuchen Sie es erneut.';
        setRegisterError(errorMsg);
        toast({
          title: "Registrierung fehlgeschlagen",
          description: errorMsg,
          variant: "destructive"
        });
      }
    } catch (error) {
      const errorMsg = 'Ein unerwarteter Fehler ist aufgetreten. Bitte versuchen Sie es erneut.';
      setRegisterError(errorMsg);
      toast({
        title: "Fehler",
        description: errorMsg,
        variant: "destructive"
      });
    }
    
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-black flex items-center justify-center p-6">
      <div className="w-full max-w-lg">
        {/* Logo */}
        <div className="flex items-center justify-center gap-2 mb-8 cursor-pointer" onClick={() => navigate('/')}>
          <img src="/assets/smartvolt-logo.png" alt="Smartvolt Academy" className="h-14" />
        </div>

        {refCode && step < 3 && (
          <div className="mb-4 rounded-xl border border-yellow-500/40 bg-gradient-to-r from-yellow-500/15 to-yellow-500/5 p-4 flex items-start gap-3" data-testid="referral-bonus-banner">
            <div className="w-9 h-9 rounded-lg bg-yellow-500 text-black flex items-center justify-center text-xl shrink-0">🎁</div>
            <div>
              <p className="text-white text-sm font-semibold">Sie wurden empfohlen!</p>
              <p className="text-yellow-200/80 text-xs mt-0.5">
                Mit der Erstbuchung eines Premium-Pakets erhalten Sie <strong className="text-yellow-400">50&nbsp;€ Digistore24-Gutschein</strong>.
              </p>
            </div>
          </div>
        )}

        {/* Progress Steps */}
        {step < 3 && (
          <div className="flex items-center justify-center gap-2 mb-6">
            {[1, 2].map((s) => (
              <div key={s} className="flex items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-all ${
                  s === step ? 'bg-yellow-500 text-black' : 
                  s < step ? 'bg-green-500 text-white' : 'bg-gray-700 text-gray-400'
                }`}>
                  {s < step ? <CheckCircle className="w-4 h-4" /> : s}
                </div>
                {s < 2 && <div className={`w-16 h-1 mx-2 ${s < step ? 'bg-green-500' : 'bg-gray-700'}`} />}
              </div>
            ))}
          </div>
        )}

        <Card className="bg-gradient-to-br from-gray-900 to-black border-yellow-600/20">
          <CardContent className="pt-6">
            {step === 1 && (
              <StepPackageSelection
                employeeCount={employeeCount}
                setEmployeeCount={setEmployeeCount}
                billingCycle={billingCycle}
                setBillingCycle={setBillingCycle}
                pricing={pricing}
                onNext={() => setStep(2)}
              />
            )}
            
            {step === 2 && (
              <StepCompanyDetails
                formData={formData}
                setFormData={setFormData}
                onNext={handleSubmit}
                onBack={() => setStep(1)}
                loading={loading}
                error={registerError}
                employeeCount={employeeCount}
                billingCycle={billingCycle}
              />
            )}
            
            {step === 3 && (
              <StepSuccess
                employeeCount={employeeCount}
                billingCycle={billingCycle}
                pricing={pricing}
              />
            )}
          </CardContent>
        </Card>

        {step < 3 && (
          <div className="mt-4 text-center text-sm text-gray-400">
            Bereits registriert?{' '}
            <span className="text-yellow-500 cursor-pointer hover:underline" onClick={() => navigate('/login')}>
              Jetzt anmelden
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default RegisterPage;
